import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../../components/AppIcon';
import { fetchMandiFactoryBySlug } from '../data';
import { fmtNum, fmtMonth } from '../../market-intelligence/utils';
import { buildMarketplaceBuyUrl } from '../../live-tender-info/matching';
import NearbyMillsDistanceList from '../../market-intelligence/NearbyMillsDistanceList';
import Footer from '../../home/components/Footer';
import { generateMandiFactoryPDF } from './generatePDF';
import { useTranslation } from '../../../hooks/useTranslation';
import LanguageSwitcher from '../../../components/ui/LanguageSwitcher';
import { useAuth } from '../../../contexts/AuthContext';

const NAVY = '#0F2238';
const AMBER = '#F59E0B';
const AMBER_DEEP = '#B45309';
const CREAM = '#FDF8F0';
const BORDER = '#E7E0D3';
const MUTED = '#78716C';
const FAINT = '#A8A29E';

const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: AMBER_DEEP }}>{children}</p>
);

const BuySugarButton = ({ label, full, disabled, onClick }) => {
  const { t } = useTranslation();
  return (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center justify-center gap-1.5 font-bold text-white rounded-xl transition-all duration-150 hover:opacity-90 active:scale-[0.97] disabled:opacity-40 disabled:cursor-not-allowed ${full ? 'w-full py-3 text-sm' : 'px-3.5 py-2 text-xs'}`}
    style={{ background: disabled ? '#A8A29E' : '#ff8c00', boxShadow: disabled ? 'none' : '0 2px 8px rgba(255,140,0,0.35)' }}
    onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#e67e00'; }}
    onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = '#ff8c00'; }}
  >
    <svg width={full ? 15 : 12} height={full ? 15 : 12} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2"><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.6 13.4a2 2 0 002 1.6h9.7a2 2 0 002-1.6L23 6H6" /></svg>
    {label || t('marketplace.buySugar', 'Buy Sugar')}
  </button>
  );
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const MandiFactoryDetail = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { factorySlug } = useParams();
  const navigate = useNavigate();
  const pdfSectionRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [factory, setFactory] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState('');
  const [buyerPincode, setBuyerPincode] = useState('');
  const [distanceKm, setDistanceKm] = useState(null);
  const [distLoading, setDistLoading] = useState(false);
  const [distError, setDistError] = useState('');
  const [highlightedGradeId, setHighlightedGradeId] = useState(null);
  const autoBuyFiredRef = useRef(false);

  const fireToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 1800); };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { factory: f, error: err } = await fetchMandiFactoryBySlug(factorySlug);
      if (cancelled) return;
      setFactory(f);
      setError(err);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [factorySlug]);

  // Arriving with ?quoteId=/?grade= (e.g. from Live Tender Info's "Buy Now on Mandi")
  // scrolls to and highlights that exact grade. With ?autoBuy=1 it also fires the
  // SAME handleBuySugar() a manual click would — no separate purchase logic — so a
  // matched grade goes straight into the existing flow without a second click.
  // autoBuy is stripped from the URL *before* handleBuySugar runs (which is what
  // computes the post-login ?next=) so an already-authenticated user's login-page
  // bounce-back can never land back on a URL that still says autoBuy=1 — otherwise
  // that would re-fire on the bounce-back and loop.
  useEffect(() => {
    if (!factory || autoBuyFiredRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const quoteId = params.get('quoteId');
    const gradeParam = params.get('grade');
    const autoBuy = params.get('autoBuy') === '1';
    if (!quoteId && !gradeParam) return;

    const match = (quoteId && factory.grades.find((g) => g.id === quoteId))
      || (gradeParam && factory.grades.find((g) => g.grade === gradeParam))
      || null;
    if (!match) return;

    autoBuyFiredRef.current = true;
    setHighlightedGradeId(match.id);
    document.getElementById(`grade-${match.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });

    if (autoBuy) {
      const keep = new URLSearchParams();
      keep.set('grade', match.grade);
      navigate(`/mandi/${factorySlug}?${keep.toString()}`, { replace: true });
      if (match.availableQty > 0) handleBuySugar(match.grade);
    }
  }, [factory]);

  // Buy Sugar continues into the Buyer Marketplace (same destination the Mandi
  // grid uses — see buildMarketplaceBuyUrl), not back onto this Mandi page.
  // Previously this only ever reached the login gate and pointed post-login
  // back at this same mill page, so a signed-in user could never actually buy;
  // now an authenticated user skips the gate entirely and an unauthenticated
  // one is returned straight to the matching Marketplace offer after login.
  const handleBuySugar = (grade) => {
    const matchedGrade = factory?.grades?.find((g) => g.grade === grade);
    const destination = buildMarketplaceBuyUrl({ factoryName: factory?.name, grade, mandiQuoteId: matchedGrade?.id });
    if (isAuthenticated) {
      navigate(destination);
      return;
    }
    fireToast(t('mandi.signInToBuy', 'Sign in to buy {{grade}} from {{factory}}', { grade, factory: factory.name }));
    setTimeout(() => navigate(`/login-authentication?next=${encodeURIComponent(destination)}`), 500);
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: factory.name, url }); return; } catch (e) { /* fall through */ }
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url);
      fireToast(t('mandi.linkCopied', 'Link copied'));
    }
  };

  const handleDownloadPdf = async () => {
    if (!factory) return;
    setDownloading(true);
    try {
      await generateMandiFactoryPDF({ factory, action: 'download' });
    } catch (e) {
      fireToast(t('mandiFactory.pdfGenerationFailed', 'Could not generate PDF — please try again.'));
    }
    setDownloading(false);
  };

  const fetchFactoryDistance = async (destPin) => {
    setDistLoading(true);
    setDistError('');
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const resp = await fetch(`${supabaseUrl}/functions/v1/calculate-route-distance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ originPincode: factory?.pincode, destinationPincode: destPin }),
      });
      if (resp?.ok) {
        const data = await resp.json();
        setDistanceKm(data?.distanceKm ?? null);
        if (data?.distanceKm == null) setDistError(t('mandiFactory.distanceErrorGeneric', 'Could not calculate distance for this pincode.'));
      } else {
        setDistError(t('mandiFactory.distanceErrorGeneric', 'Could not calculate distance for this pincode.'));
      }
    } catch {
      setDistError(t('mandiFactory.distanceErrorRetry', 'Could not calculate distance right now. Please try again.'));
    } finally {
      setDistLoading(false);
    }
  };

  const handleBuyerPincodeChange = (value) => {
    const pin = value?.replace(/\D/g, '').substring(0, 6);
    setBuyerPincode(pin);
    setDistanceKm(null);
    setDistError('');
    if (pin?.length === 6 && factory?.pincode?.length === 6) {
      fetchFactoryDistance(pin);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#F3EFE4', fontFamily: "'DM Sans', sans-serif" }}>
      <Helmet>
        <title>{factory ? `${factory.name} — Live Rate | SugarHathi Mandi` : 'SugarHathi Mandi'}</title>
        <meta name="description" content={factory ? `Live sugar rates, quota and factory details for ${factory.name}. Buy direct, verified by SugarHathi.` : 'Live sugar rates on SugarHathi Mandi.'} />
        <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
      </Helmet>

      <div className="sticky top-0 z-30 bg-white border-b" style={{ borderColor: BORDER }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate('/mandi')} className="flex items-center gap-1.5 text-sm font-semibold flex-shrink-0" style={{ color: MUTED }}>
            <Icon name="ArrowLeft" size={16} color={MUTED} />
            {t('mandiFactory.backToMandi', 'Mandi')}
          </button>
          <div className="flex items-center gap-2 flex-shrink-0">
            <LanguageSwitcher />
            <button onClick={handleShare} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold border-2" style={{ background: 'transparent', borderColor: '#1e4a72', color: '#1e4a72' }}>
              <Icon name="Share2" size={14} color="#1e4a72" />
              {t('mandiFactory.share', 'Share')}
            </button>
            <button onClick={handleDownloadPdf} disabled={!factory || downloading} className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white disabled:opacity-50" style={{ background: '#ff8c00' }}>
              <Icon name="Download" size={14} color="#fff" />
              {downloading ? t('mandiFactory.generatingPdf', 'Generating…') : t('mandiFactory.downloadPdf', 'Download PDF')}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="w-8 h-8 rounded-full border-2 animate-spin mb-3" style={{ borderColor: '#fde68a', borderTopColor: AMBER }} />
            <p className="text-sm" style={{ color: MUTED }}>{t('mandiFactory.loadingListing', 'Loading listing…')}</p>
          </div>
        )}

        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <Icon name="AlertTriangle" size={28} color="#DC2626" />
            <p className="text-sm mt-3" style={{ color: MUTED }}>{error}</p>
          </div>
        )}

        {!loading && !error && factory && (
          <div ref={pdfSectionRef} className="rounded-2xl p-6" style={{ background: '#FFFFFF', border: `1px solid ${BORDER}`, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>

            {/* 1. Name + Address */}
            <div className="flex items-center gap-2 mb-2">
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#FFF3E0', color: AMBER_DEEP, border: '1px solid #FDE1B8' }}>{factory.millCode}</span>
              <span className="text-xs" style={{ color: FAINT }}>{factory.district}, {factory.state}</span>
            </div>
            <h1 className="text-xl font-bold mb-1" style={{ color: NAVY }}>{factory.name}</h1>
            {factory.address && <p className="text-sm mb-5" style={{ color: '#44403C' }}>{factory.address}</p>}

            {/* 2. Current Quota — the single most recent figure (a newer fortnight replaces
                the last complete month here; see mandi/data.js) */}
            {factory.quotaQty != null && (
              <div className="rounded-xl p-4 mb-5" style={{ background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 60%, #fde68a22 100%)', border: '1px solid #fde68a' }}>
                <p className="text-xs font-medium mb-0.5" style={{ color: '#78350f' }}>
                  {factory.quotaMonthLabel} {t(factory.quotaIsFortnight ? 'mandi.fortnightQuotaLabel' : 'mandi.quotaLabel', factory.quotaIsFortnight ? 'Fortnight Quota' : 'Quota')}
                </p>
                <p className="text-2xl font-bold" style={{ color: NAVY }}>{factory.quotaQty.toLocaleString('en-IN')} <span className="text-base font-semibold" style={{ color: AMBER_DEEP }}>MT</span></p>
              </div>
            )}

            {/* 3. Grade, Rate, Qty — full input from factory relations officer */}
            <section className="mb-5">
              <SectionLabel>{t('mandiFactory.availableForSale', 'Available for Sale')}</SectionLabel>
              <div className="space-y-3">
                {factory.grades.map((g) => {
                  const sold = g.availableQty <= 0;
                  const todayStr = new Date()?.toISOString()?.slice(0, 10);
                  const expired = !sold && g.liftingDate && g.liftingDate < todayStr;
                  const disabled = sold || expired;
                  const highlighted = g.id === highlightedGradeId;
                  return (
                    <div
                      key={g.id}
                      id={`grade-${g.id}`}
                      className="rounded-xl p-4 transition-shadow duration-300"
                      style={{ background: CREAM, border: `1px solid ${highlighted ? AMBER : BORDER}`, boxShadow: highlighted ? '0 0 0 3px rgba(245,158,11,0.35)' : 'none' }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-base font-black" style={{ color: NAVY }}>{t('common.grade', 'Grade')} {g.grade}</span>
                        <BuySugarButton disabled={disabled} label={sold ? t('mandi.soldOut', 'Sold out') : expired ? t('mandi.offerExpired', 'Offer Expired') : t('marketplace.buySugar', 'Buy Sugar')} onClick={() => handleBuySugar(g.grade)} />
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs" style={{ color: MUTED }}>
                        <div>{t('mandi.rateExclGst', 'Rate Excl GST')}<br /><span className="font-semibold" style={{ color: NAVY }}>&#8377;{g.rateExclGst.toLocaleString('en-IN')}/MT</span></div>
                        <div>{t('mandi.rateInclGst', 'Rate Incl GST')}<br /><span className="font-semibold" style={{ color: NAVY }}>&#8377;{g.rateInclGst.toLocaleString('en-IN')}/MT</span></div>
                        <div>{t('marketplace.paymentDate', 'Payment Date')}<br /><span className="font-semibold" style={{ color: NAVY }}>{fmtDate(g.paymentDate)}</span></div>
                        <div>{t('marketplace.liftingDate', 'Lifting Date')}<br /><span className="font-semibold" style={{ color: NAVY }}>{fmtDate(g.liftingDate)}</span></div>
                      </div>
                      <p className="text-xs mt-2" style={{ color: disabled ? FAINT : '#DC2626', fontWeight: 700 }}>{sold ? t('mandi.soldOut', 'Sold out') : expired ? t('mandi.offerExpired', 'Offer Expired') : t('mandi.mtAvailable', '{{qty}} MT available', { qty: g.availableQty })}</p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Enter-pincode distance calculator, ported from market-intelligence/factory */}
            <section className="mb-5">
              <div className="rounded-xl p-4" style={{ background: '#EFF6FF', border: '1px solid #BFDBFE' }}>
                <p className="text-sm font-bold mb-1" style={{ color: NAVY }}>{factory.pincode ? t('mandiFactory.distanceTitleWithPincode', 'Distance from this Factory [{{pincode}}] to your chosen Location', { pincode: factory.pincode }) : t('mandiFactory.distanceTitleNoPincode', 'Distance from this Factory to your chosen Location')}</p>
                {factory.pincode ? (
                  <>
                    <p className="text-xs mb-3" style={{ color: MUTED }}>{t('mandiFactory.enterPincodeHint', 'Enter your pincode to see the approximate road distance from this factory.')}</p>
                    <div className="flex gap-2 mb-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={buyerPincode}
                        onChange={(e) => handleBuyerPincodeChange(e?.target?.value)}
                        placeholder={t('mandiFactory.pincodePlaceholder', 'Your 6-digit pincode')}
                        className="flex-1 rounded-[10px] border px-3 py-2 text-sm font-semibold"
                        style={{ borderColor: buyerPincode && buyerPincode?.length !== 6 ? '#F8C9C9' : BORDER, background: '#FFFFFF', color: NAVY }}
                      />
                    </div>
                    {distLoading && (
                      <div className="flex items-center gap-2 py-1">
                        <div className="w-3 h-3 border-2 rounded-full animate-spin" style={{ borderColor: '#ff8c00', borderTopColor: 'transparent' }} />
                        <p className="text-xs" style={{ color: MUTED }}>{t('mandiFactory.calculatingDistance', 'Calculating road distance...')}</p>
                      </div>
                    )}
                    {!distLoading && distError && <p className="text-xs" style={{ color: '#B91C1C' }}>{distError}</p>}
                    {!distLoading && !distError && distanceKm !== null && (
                      <div className="rounded-[10px] p-3 text-center" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                        <p className="text-xs font-medium" style={{ color: MUTED }}>{t('mandiFactory.approxRoadDistance', 'Approx. Road Distance')}</p>
                        <p className="text-lg font-black" style={{ color: '#15803D' }}>{distanceKm} km</p>
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs" style={{ color: FAINT }}>{t('mandiFactory.distanceNotAvailable', 'Distance calculator not yet available for this factory.')}</p>
                )}
              </div>
            </section>

            {/* 6. Past 12 months quota */}
            {factory.past12.length > 0 && (
              <section className="mb-5">
                <SectionLabel>{t('mandiFactory.past12MonthsQuota', 'Past 12 Months Quota')}</SectionLabel>
                <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
                  <div className="grid grid-cols-2 px-3 py-2" style={{ background: '#F5EDDD' }}>
                    <span className="text-[11px] font-semibold" style={{ color: MUTED }}>{t('mandiFactory.monthLabel', 'Month')}</span>
                    <span className="text-[11px] font-semibold text-right" style={{ color: MUTED }}>{t('mandiFactory.allocationLabel', 'Allocation')}</span>
                  </div>
                  {factory.past12.slice().reverse().map((r, idx) => (
                    <div key={idx} className="grid grid-cols-2 px-3 py-2" style={{ background: idx % 2 === 0 ? '#FFFFFF' : '#FDF6E9' }}>
                      <span className="text-sm" style={{ color: '#44403C' }}>{r.month}</span>
                      <span className="text-sm font-semibold text-right" style={{ color: NAVY }}>{r.qty.toLocaleString('en-IN')} MT</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 7. Recent news */}
            <section className="mb-5">
              <SectionLabel>{t('mandiFactory.recentNews', 'Recent News')}</SectionLabel>
              <div className="rounded-xl p-3" style={{ border: `1px solid ${BORDER}` }}>
                {factory.news.length === 0 ? (
                  <p className="text-xs" style={{ color: FAINT }}>{t('mandiFactory.noRecentNews', 'No recent news for this mill yet.')}</p>
                ) : (
                  factory.news.map((n, idx) => (
                    <a key={idx} href={n.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2" style={{ marginTop: idx > 0 ? 10 : 0 }}>
                      <Icon name="ExternalLink" size={13} color={AMBER_DEEP} className="flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <span className="text-sm block" style={{ color: '#292524' }}>{n.title}</span>
                        {n.date && <p className="text-xs mt-0.5" style={{ color: FAINT }}>{n.date}</p>}
                      </div>
                    </a>
                  ))
                )}
              </div>
            </section>

            {/* SugarHathi Summary — moved here, just below Recent News */}
            {factory.metrics && (
              <section className="mb-5">
                <div className="rounded-xl p-5" style={{ background: '#E7F7EF', border: '1px solid #B7E8CE' }}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(180deg, #F59E0B 0%, #D97706 100%)' }}>
                      <Icon name="Zap" size={14} color="#FFFFFF" />
                    </div>
                    <span className="text-sm font-bold" style={{ color: NAVY }}>{t('mandiFactory.summaryTitle', 'SugarHathi summary')}</span>
                  </div>
                  <p className="text-sm leading-relaxed" style={{ color: '#365314' }}>
                    {factory.metrics.latest_qty_mt < factory.metrics.max_qty_mt
                      ? t('mandiFactory.allocationTrendDeclined', 'Allocation declined from a peak of {{peak}} MT ({{peakMonth}}) to {{latest}} MT in {{latestMonth}}.', { peak: fmtNum(factory.metrics.max_qty_mt), peakMonth: fmtMonth(factory.metrics.max_qty_month), latest: fmtNum(factory.metrics.latest_qty_mt), latestMonth: fmtMonth(factory.metrics.latest_month) })
                      : t('mandiFactory.allocationTrendGrew', 'Allocation reached a new peak of {{peak}} MT ({{peakMonth}}) to {{latest}} MT in {{latestMonth}}.', { peak: fmtNum(factory.metrics.max_qty_mt), peakMonth: fmtMonth(factory.metrics.max_qty_month), latest: fmtNum(factory.metrics.latest_qty_mt), latestMonth: fmtMonth(factory.metrics.latest_month) })}
                    {factory.ranks && ' ' + (factory.ranks.state_rank
                      ? t('mandiFactory.rankNationalAndState', 'Ranks {{rank}} of {{total}} nationally, {{stateRank}} within {{state}}.', { rank: factory.ranks.national_rank, total: factory.ranks.totalMills, stateRank: factory.ranks.state_rank, state: factory.state })
                      : t('mandiFactory.rankNationalOnly', 'Ranks {{rank}} of {{total}} nationally.', { rank: factory.ranks.national_rank, total: factory.ranks.totalMills }))}
                  </p>
                </div>
              </section>
            )}

            {/* 5. Distance table */}
            <section className="mb-5">
              <NearbyMillsDistanceList millId={factory.millId} millName={factory.name} accentColor={AMBER} titleColor={AMBER_DEEP} />
            </section>

            {/* 8. SugarHathi service buttons — same 6 buttons + colors as the PDF's drawCTAButtons() */}
            <section>
              <SectionLabel>{t('mandiFactory.sugarhathiServices', 'SugarHathi Services')}</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                {[
                  [t('hero.browseSugarOffers', 'Browse Sugar Offers'), '/sugar-marketplace-dashboard', '#F97316'],
                  [t('hero.browseVerifiedTrucks', 'Browse Verified Trucks'), '/truck-marketplace', '#F97316'],
                  [t('hero.sellYourSugar', 'Sell Your Sugar'), '/sell-your-sugar', '#F97316'],
                  [t('mandiFactory.govtQuotaTracker', 'Govt Sugar Quota Tracker'), '/market-intelligence', '#3B82F6'],
                  [t('mandiFactory.registerFree', 'Register for Free'), '/register', '#128C7E'],
                ].map(([label, path, color]) => (
                  <button
                    key={path}
                    onClick={() => navigate(path)}
                    className="text-xs font-bold px-3 py-2.5 rounded-xl text-center text-white transition-opacity duration-150 hover:opacity-90"
                    style={{ background: color }}
                  >
                    {label}
                  </button>
                ))}
                <a
                  href="https://wa.me/917989953468?text=Hi%2C%20I%27d%20like%20to%20register%20on%20SugarHathi"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-bold px-3 py-2.5 rounded-xl text-center text-white transition-opacity duration-150 hover:opacity-90 flex items-center justify-center"
                  style={{ background: '#128C7E' }}
                >
                  {t('mandiFactory.registerWhatsapp', 'Register via WhatsApp')}
                </a>
              </div>
            </section>
          </div>
        )}
      </main>

      {/* 9. Footer note */}
      <Footer />

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white shadow-lg" style={{ background: NAVY }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default MandiFactoryDetail;



